/**
 * @fileoverview Own browser navigation memory, view preferences, and refresh notifications shared across Intake surfaces. Saved paths connect queue, batch, review, and lead-detail navigation. These records are local UI context rather than canonical lead or workflow state. Consumers must still load current backend data when following a remembered selection.
 */

const NAVIGATION_STORAGE_KEY = 'intake_workspace_navigation_v1';
const VIEW_STATE_STORAGE_PREFIX = 'intake_workspace_view_state_v1';

const DEFAULT_PATHS = {
  intake: '/intake/leads',
  communications: '/intake/communications',
  reviews: '/intake/reviews',
  batches: '/intake/batches',
};

const VIEW_KEYS = new Set(Object.keys(DEFAULT_PATHS));



function sessionStorageOrNull() {
  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
}



function viewForPathname(pathname) {
  if (/^\/intake\/?$/.test(pathname || '')) return 'communications';
  if (/^\/intake\/leads\/?$/.test(pathname || '')) return 'intake';
  if (/^\/intake\/communications\/?$/.test(pathname || '')) return 'communications';
  if (/^\/intake\/reviews(?:\/[^/]+)?\/?$/.test(pathname || '')) return 'reviews';
  if (/^\/intake\/batches(?:\/[^/]+)?\/?$/.test(pathname || '')) return 'batches';
  return null;
}



function pathnameFromPath(path) {
  return String(path || '').split(/[?#]/, 1)[0];
}



function validStoredPath(path, expectedView) {
  return typeof path === 'string' && viewForPathname(pathnameFromPath(path)) === expectedView;
}



function defaultNavigationState() {
  return {
    lastView: 'communications',
    paths: { ...DEFAULT_PATHS },
  };
}



function readNavigationState() {
  const fallback = defaultNavigationState();
  const storage = sessionStorageOrNull();
  if (!storage) return fallback;

  try {
    const parsed = JSON.parse(storage.getItem(NAVIGATION_STORAGE_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') return fallback;
    const paths = { ...DEFAULT_PATHS };
    Object.keys(paths).forEach((view) => {
      if (validStoredPath(parsed.paths?.[view], view)) {
        paths[view] = parsed.paths[view];
      }
    });
    const lastView = VIEW_KEYS.has(parsed.lastView) && validStoredPath(parsed.paths?.[parsed.lastView], parsed.lastView)
      ? parsed.lastView : 'communications';
    return { lastView, paths };
  } catch (error) {
    return fallback;
  }
}



function writeNavigationState(state) {
  const storage = sessionStorageOrNull();
  if (!storage) return;
  try {
    storage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    // Intake navigation should still work when browser storage is unavailable.
  }
}



export function isIntakeWorkspacePath(pathname) {
  return Boolean(viewForPathname(pathname));
}



export function rememberIntakeWorkspaceLocation(location) {
  const view = viewForPathname(location?.pathname);
  if (!view) return getLastIntakeWorkspacePath();

  const path = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const state = readNavigationState();
  const nextState = {
    lastView: view,
    paths: { ...state.paths, [view]: path },
  };
  writeNavigationState(nextState);
  return path;
}



export function getLastIntakeWorkspacePath() {
  const state = readNavigationState();
  return state.paths[state.lastView] || DEFAULT_PATHS.communications;
}



export function getIntakeViewPath(view) {
  const state = readNavigationState();
  return state.paths[view] || DEFAULT_PATHS[view] || DEFAULT_PATHS.communications;
}



export function readIntakeViewState(view, fallback = {}) {
  if (!VIEW_KEYS.has(view)) return fallback;
  const storage = sessionStorageOrNull();
  if (!storage) return fallback;
  try {
    const parsed = JSON.parse(storage.getItem(`${VIEW_STATE_STORAGE_PREFIX}:${view}`) || 'null');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
}



export function writeIntakeViewState(view, state) {
  if (!VIEW_KEYS.has(view)) return;
  const storage = sessionStorageOrNull();
  if (!storage) return;
  try {
    storage.setItem(`${VIEW_STATE_STORAGE_PREFIX}:${view}`, JSON.stringify(state));
  } catch (error) {
    // Keep the view usable if storage is full or disabled.
  }
}
